import React, { useState, useEffect } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { useFiling } from "../contexts/FilingContext";
import { SideType, sideTypes } from "../models/SideType";
import { Select } from "../design/select";
import { PlusIcon } from "@heroicons/react/20/solid";

interface Props {
  className?: string;
  children?: React.ReactNode;
}

interface Errors {
  name?: string;
  dueDate?: string;
  side?: string;
}

const CreateFilingButton: React.FC<Props> = ({ className, children }) => {
  const { createFiling } = useFiling();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState<string>("");
  const [filingDate, setFilingDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );
  const [side, setSide] = useState<SideType>("me");
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!name.trim()) {
      newErrors.name = "A name is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsUploading(true);

    try {
      await createFiling(null, {
        name,
        filingDate: filingDate ? new Date(filingDate) : undefined,
        side,
      });
      clearForm();
    } catch (error) {
      setErrors({ name: "Failed to create filing. Please try again." });
    }

    setIsUploading(false);
  };

  const clearForm = () => {
    setName("");
    setFilingDate("");
    setSide("");
    setErrors({});
    setIsOpen(false);
  };

  return (
    <>
      <Button
        className={className}
        type="button"
        onClick={() => setIsOpen(true)}
      >
        {children ?? (
          <div className="flex items-center gap-2">
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:block">Create Filing</span>
          </div>
        )}
      </Button>
      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Create Filing</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
              </Field>
              <Field>
                <Label htmlFor="date">Filing Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={filingDate}
                  onChange={(e) => setFilingDate(e.target.value)}
                />
              </Field>
              <Field>
                <Label>Side</Label>
                <Select
                  value={side}
                  onChange={(e) => setSide(e.target.value as SideType)}
                >
                  <option value="" disabled>
                    Select side
                  </option>
                  {Object.entries(sideTypes).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </Select>
                {errors.side && <ErrorMessage>{errors.side}</ErrorMessage>}
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button disabled={!name.trim() || isUploading} onClick={handleSubmit}>
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateFilingButton;
